import { FC } from 'react';
import { AmlHistoryItemData } from '@/redux/models';
import { getTokenTitelize } from '@/helpers';
import { AmlFlowEnum } from '@/api';
import {
  AmlCheckBalance,
  AmlCheckGeneralInfo,
  AmlCheckAssets,
  AmlMistrackInfo,
} from './components';
import styles from './AddressInfo.module.sass';

interface TokenListProps {
  list?: Record<string, any>;
}

const TokenList: FC<TokenListProps> = ({ list = {} }) => {
  if (!list && !Object.keys(list)) {
    return null;
  }

  const filledTokenKeys = Object.keys(list).filter(
    (contract: string) => list[contract].length > 0,
  );

  return (
    <>
      {filledTokenKeys.map((contract: string) => (
        <div className={styles.box} key={contract}>
          <AmlCheckAssets
            protocol={getTokenTitelize(contract)}
            assets={list[contract]}
          />
        </div>
      ))}
    </>
  );
};

interface AddressInfoProps {
  item?: AmlHistoryItemData;
  isLoading?: boolean;
}

const AddressInfo: FC<AddressInfoProps> = ({ item, isLoading }) => {
  const hasBalanceInfo = () => {
    return (
      item?.addressDetailsData?.balance_usd ||
      item?.addressDetailsData?.balance ||
      item?.addressDetailsData?.balance_approximate
    );
  };

  const hasGeneralInfo = () => {
    return item?.addressOwner || item?.addressDetailsData?.n_txs;
  };

  const hasMistTrackData = () => {
    return (
      [
        AmlFlowEnum.ADVANCED,
        AmlFlowEnum.LIGHT,
        AmlFlowEnum.LITE,
        AmlFlowEnum.FAST_LIMITED,
        AmlFlowEnum.ADVANCED_LIMITED,
      ].includes(item?.flow || AmlFlowEnum.ADVANCED) &&
      item?.extras &&
      (!!item?.extras?.tags?.length ||
        !!Object.keys(item?.extras?.services).length)
    );
  };

  const hasSidebarInfo = () => {
    return hasBalanceInfo() || hasGeneralInfo() || hasMistTrackData;
  };

  if (!isLoading && !hasSidebarInfo()) {
    return (
      <div className={styles.box}>
        <div>
          <b>We didn&apos;t find anything</b>
        </div>
        <div>We checked our databases and didn&apos;t find any information</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.box}>
        <div className={styles.skeleton}>
          <div className={styles.skeletonTitle} />
          <div className={styles.skeletonLine} />
          <div className={styles.skeletonLine} />
        </div>
      </div>
    );
  }

  return (
    <>
      {!!hasBalanceInfo() && (
        <div className={styles.box}>
          <AmlCheckBalance
            data={item?.addressDetailsData}
            asset={item?.asset || ''}
            network={item?.network || ''}
          />
        </div>
      )}

      {!!hasMistTrackData() && (
        <div className={styles.box}>
          <AmlMistrackInfo info={item?.extras} amlFlow={item?.flow} />
        </div>
      )}

      {!!hasGeneralInfo() && (
        <div className={styles.box}>
          <AmlCheckGeneralInfo
            data={item?.addressDetailsData}
            owner={item?.addressOwner}
          />
        </div>
      )}

      <TokenList list={item?.addressDetailsData?.tokens} />
    </>
  );
};

export default AddressInfo;
